/**
 * Combat Effectiveness Modifiers - Research-Validated Constants
 * PSP/CRE Tool v3.0
 * 
 * Based on:
 * - Dupuy Institute TNDM/QJM models (600+ historical engagements)
 * - Ukraine 2022-2024 conflict data (RUSI, Mediazona, CSIS)
 * - NATO AMedP-9.1, TCCC studies
 * - Lanchester's Laws applications
 * 
 * Author: Dr. Tatar Radu MD
 * Date: December 5, 2024
 * Version: 1.2 (March 28, 2026)
 *
 * v1.2 CHANGES (March 28, 2026) — ENH-043: Per-brigade force quality resolution:
 * - Added: resolvePerBrigadeQuality(brigadeConfig, force) — shared utility function
 *   Walks Corps → Division → Brigade override chain to return fully-resolved
 *   force quality object for a specific brigade. Used by casualtyCalculator.js
 *   to replace the single-global force_quality passed to all brigades (line 7154 fix).
 *   Brigade-level overrides are configured via Tab1 UnitEditDialog → ForceQualityOverrideSection.
 *
 * v1.1 CHANGES (February 11, 2026):
 * - Added: CLS_TRAINING_LEVELS - Combat Lifesaver training levels with DOW multipliers
 * - Added: DEFAULT_FORCE_QUALITY - Default force quality values for Corps-level cascade
 * - Both moved from Tab1_ForceComposition.jsx local constants to this file (v4.3.5 cleanup)
 * - Both exported as named exports and included in default export object
 *
 * CRITICAL: These modifiers are MULTIPLICATIVE
 * Combined modifier = training × equipment × morale × forceRatio × experience
 */

// ============================================================================
// TRAINING LEVEL MODIFIERS
// ============================================================================

/**
 * Training level determines casualty susceptibility
 * 
 * Research basis:
 * - Dupuy CEV: Wehrmacht 1.2-1.55x over US/UK, 2.5-3.0x over Soviets
 * - RUSI Ukraine 2025: Trained units 5-10% casualties vs untrained 50%
 * - Mediazona: Mobilized troops 10.5% of deaths, 4.5 month avg survival
 * 
 * Higher values = MORE casualties (multiplier on base rate)
 */
export const TRAINING_LEVELS = {
  ELITE: {
    value: 'ELITE',
    label: 'Elite / Special Forces',
    description: 'SOF, paratroopers, experienced professional units with extensive combined arms training',
    casualty_multiplier: 0.60,      // 40% fewer casualties
    effectiveness_value: 1.8,        // Dupuy CEV equivalent
    inflict_multiplier: 1.40,        // 40% more effective at causing enemy casualties
    examples: 'Romanian Special Operations, NATO SOF, experienced brigade combat teams',
    confidence: 'HIGH'
  },
  VETERAN: {
    value: 'VETERAN',
    label: 'Veteran / Combat Experienced',
    description: '12+ months combat experience, proven in multiple engagements',
    casualty_multiplier: 0.80,
    effectiveness_value: 1.3,
    inflict_multiplier: 1.20,
    examples: 'Units rotated from active operations, experienced NCO corps',
    confidence: 'HIGH'
  },
  REGULAR: {
    value: 'REGULAR',
    label: 'Regular / Professional',
    description: 'Standard professional military training, peacetime readiness',
    casualty_multiplier: 1.00,       // BASELINE
    effectiveness_value: 1.0,
    inflict_multiplier: 1.00,
    examples: 'Standard Romanian Army brigades, NATO regular forces',
    confidence: 'HIGH'
  },
  GREEN: {
    value: 'GREEN',
    label: 'Green / Conscript',
    description: 'Basic training complete but no combat experience, conscript forces',
    casualty_multiplier: 1.50,       // 50% more casualties
    effectiveness_value: 0.65,
    inflict_multiplier: 0.75,
    examples: 'Fresh conscripts, reserve units without recent training',
    confidence: 'HIGH'
  },
  MINIMAL: {
    value: 'MINIMAL',
    label: 'Minimally Trained',
    description: '2-6 weeks basic training only, mobilized civilians',
    casualty_multiplier: 2.00,       // 2x casualties
    effectiveness_value: 0.50,
    inflict_multiplier: 0.50,
    examples: 'Emergency mobilization, territorial defense without full training',
    confidence: 'MEDIUM'
  },
  UNTRAINED: {
    value: 'UNTRAINED',
    label: 'Untrained / Assault Infantry',
    description: 'Minimal to no training, penal units, human wave tactics',
    casualty_multiplier: 2.50,       // 2.5x casualties (RUSI data supports up to 10x in specific situations)
    effectiveness_value: 0.35,
    inflict_multiplier: 0.30,
    examples: 'Wagner assault groups, emergency militia, prison recruits',
    confidence: 'MEDIUM',
    warning: 'Extremely high casualty rates - consider if scenario is realistic'
  }
};

// ============================================================================
// EQUIPMENT QUALITY MODIFIERS
// ============================================================================

/**
 * Equipment quality affects both protection (KIA/DOW rates) and lethality
 * 
 * Research basis:
 * - Body armor: 86% KIA reduction (ESAPI plates vs unarmored)
 * - Vehicle armor: 70-80% crew survival (Western) vs 10-35% (Soviet design)
 * - Trophy APS: 90%+ ATGM intercept rate
 * - Ukraine W:K ratio: 5:1 (Ukrainian) vs 3:1 (Russian) - 40% difference from equipment/medical
 */
export const EQUIPMENT_LEVELS = {
  SUPERIOR: {
    value: 'SUPERIOR',
    label: 'Superior / NATO Modern',
    description: 'Full modern kit: modern IFV/MRAP, night vision, drones, active protection. Body armor is set separately.',
    casualty_multiplier: 0.75,       // 25% fewer total casualties
    kia_multiplier: 0.50,            // WIRED 2026-10-25 (equipmentKillEffect.js): acts on the killed share of the mounted part of the units only; soldier body armor is the separate body armor setting
    dow_multiplier: 0.70,            // UNWIRED (not read by the calculator); DOW comes from the hazard model and the care settings
    lethality_bonus: 1.25,           // 25% more effective at causing casualties
    examples: 'US Army BCT, NATO rapid reaction forces, well-equipped Romanian units',
    confidence: 'HIGH'
  },
  MODERN: {
    value: 'MODERN',
    label: 'Modern / Current Generation',
    description: 'Current generation weapons, vehicles, optics and communications. Body armor is set separately.',
    casualty_multiplier: 0.90,
    kia_multiplier: 0.70,
    dow_multiplier: 0.85,
    lethality_bonus: 1.10,
    examples: 'Standard NATO forces, Romanian mechanized brigades',
    confidence: 'HIGH'
  },
  ADEQUATE: {
    value: 'ADEQUATE',
    label: 'Adequate / Standard',
    description: 'Functional equipment meeting basic requirements, mixed generations',
    casualty_multiplier: 1.00,       // BASELINE
    kia_multiplier: 1.00,
    dow_multiplier: 1.00,
    lethality_bonus: 1.00,
    examples: 'Most conventional forces, reserve units with standard issue',
    confidence: 'HIGH'
  },
  OUTDATED: {
    value: 'OUTDATED',
    label: 'Outdated / Previous Generation',
    description: 'Older weapons and vehicles, limited vehicle protection. Body armor is set separately.',
    casualty_multiplier: 1.20,       // 20% more casualties
    kia_multiplier: 1.30,            // Older armor less effective
    dow_multiplier: 1.15,
    lethality_bonus: 0.90,
    examples: 'Soviet-era equipment without upgrades, T-72B without ERA',
    confidence: 'HIGH'
  },
  POOR: {
    value: 'POOR',
    label: 'Poor / Deficient',
    description: 'Significant equipment shortages, obsolete weapons and vehicles. Body armor is set separately.',
    casualty_multiplier: 1.40,       // 40% more casualties
    kia_multiplier: 1.60,            // Much higher KIA rate
    dow_multiplier: 1.30,
    lethality_bonus: 0.70,
    examples: 'Mobilized units with equipment shortages, militia with personal gear only',
    confidence: 'MEDIUM'
  },
  CRITICAL_SHORTAGE: {
    value: 'CRITICAL_SHORTAGE',
    label: 'Critical Shortage',
    description: 'Severe shortages in weapons, vehicles, optics and communications; weapons sharing. Body armor is set separately.',
    casualty_multiplier: 1.70,
    kia_multiplier: 2.00,            // WIRED 2026-10-25 (equipmentKillEffect.js): killed share of the mounted part of the units only; body armor is the separate body armor setting
    dow_multiplier: 1.50,
    lethality_bonus: 0.50,
    examples: 'Russian mobilized units 2022, forces without body armor/helmets',
    confidence: 'MEDIUM',
    warning: 'Indicates severe logistical failure'
  }
};

// ============================================================================
// MORALE / COHESION MODIFIERS
// ============================================================================

/**
 * Morale affects both combat effectiveness and casualty rates
 * 
 * Research basis:
 * - Dupuy morale factors: 0.80-1.20x effectiveness
 * - Fennell study: -0.949 correlation morale vs losses
 * - Historical: "More killed in rout than battle" - up to 10x pursuit casualties
 * - US FM 105-5: 20% casualties = 40% probability of attacker break
 */
export const MORALE_LEVELS = {
  FANATICAL: {
    value: 'FANATICAL',
    label: 'Fanatical / Excellent',
    description: 'Exceptional motivation, will fight to last man, ideological commitment',
    casualty_multiplier: 0.85,       // Fight smarter, better discipline under fire
    effectiveness_multiplier: 1.20,
    break_threshold: 0.60,           // Will sustain 60% casualties before breaking
    retreat_casualties: 1.0,         // Organized even in retreat
    examples: 'Azov Regiment, elite guard units, highly motivated defenders',
    confidence: 'HIGH'
  },
  HIGH: {
    value: 'HIGH',
    label: 'High Morale',
    description: 'Strong unit cohesion, confident in mission, good leadership',
    casualty_multiplier: 0.90,
    effectiveness_multiplier: 1.10,
    break_threshold: 0.45,
    retreat_casualties: 1.1,
    examples: 'Well-led professional units, units with recent victories',
    confidence: 'HIGH'
  },
  NORMAL: {
    value: 'NORMAL',
    label: 'Normal / Average',
    description: 'Standard military morale, adequate motivation',
    casualty_multiplier: 1.00,       // BASELINE
    effectiveness_multiplier: 1.00,
    break_threshold: 0.30,           // Standard 30% casualty threshold
    retreat_casualties: 1.2,
    examples: 'Most regular forces in standard conditions',
    confidence: 'HIGH'
  },
  LOW: {
    value: 'LOW',
    label: 'Low Morale',
    description: 'Poor motivation, questioning leadership, supply problems',
    casualty_multiplier: 1.25,       // Mistakes increase casualties
    effectiveness_multiplier: 0.80,
    break_threshold: 0.20,           // Break earlier
    retreat_casualties: 1.5,
    examples: 'Units with supply shortages, poor leadership, extended combat fatigue',
    confidence: 'HIGH'
  },
  BROKEN: {
    value: 'BROKEN',
    label: 'Broken / Shattered',
    description: 'Unit cohesion lost, soldiers acting individually, mass surrenders',
    casualty_multiplier: 2.00,
    effectiveness_multiplier: 0.50,
    break_threshold: 0.10,
    retreat_casualties: 3.0,         // 3x casualties during retreat
    examples: 'Units after severe defeat, encircled units, mass desertion',
    confidence: 'MEDIUM',
    warning: 'Unit may be combat ineffective'
  },
  ROUTED: {
    value: 'ROUTED',
    label: 'Routed / Fleeing',
    description: 'Complete rout, every man for himself, no organized resistance',
    casualty_multiplier: 4.00,       // Historical: 4-10x casualties in rout
    effectiveness_multiplier: 0.10,
    break_threshold: 0.00,           // Already broken
    retreat_casualties: 5.0,         // Pursuit devastates routed forces
    examples: 'Kharkiv rout 2022, historical cavalry pursuits',
    confidence: 'MEDIUM',
    warning: 'CATASTROPHIC - unit effectively destroyed'
  }
};

// ============================================================================
// RELATIVE FORCE QUALITY (vs Enemy)
// ============================================================================

/**
 * Relative combat power affects casualty exchange ratios
 * 
 * Research basis:
 * - Lanchester Square Law: strength = N² (quality-adjusted)
 * - Dupuy: No direct force ratio → exchange ratio relationship
 * - Quality differentials dominate quantity in modern warfare
 * - Ukraine: 2:1 to 7:1 exchange ratios depending on quality differential
 */
export const FORCE_SUPERIORITY = {
  OVERWHELMING: {
    value: 'OVERWHELMING',
    label: 'Overwhelming Superiority',
    description: 'Massive qualitative advantage across all domains',
    friendly_casualty_multiplier: 0.50,  // Half casualties
    enemy_casualty_multiplier: 2.00,     // Double enemy casualties
    exchange_ratio: '1:4 to 1:7',
    examples: 'Desert Storm coalition vs Iraq, modern military vs insurgents',
    confidence: 'HIGH'
  },
  SUPERIOR: {
    value: 'SUPERIOR',
    label: 'Superior Forces',
    description: 'Clear advantage in training, equipment, and/or firepower',
    friendly_casualty_multiplier: 0.75,
    enemy_casualty_multiplier: 1.50,
    exchange_ratio: '1:2 to 1:3',
    examples: 'Ukrainian defense at Avdiivka, well-prepared NATO defense',
    confidence: 'HIGH'
  },
  EQUAL: {
    value: 'EQUAL',
    label: 'Equal / Peer Forces',
    description: 'Roughly equivalent capabilities, outcome depends on tactics',
    friendly_casualty_multiplier: 1.00,  // BASELINE
    enemy_casualty_multiplier: 1.00,
    exchange_ratio: '1:1',
    examples: 'Peer-to-peer conventional warfare, early Ukraine 2022',
    confidence: 'HIGH'
  },
  INFERIOR: {
    value: 'INFERIOR',
    label: 'Inferior Forces',
    description: 'Enemy has qualitative advantages',
    friendly_casualty_multiplier: 1.30,
    enemy_casualty_multiplier: 0.80,
    exchange_ratio: '2:1 to 3:1 against',
    examples: 'Attacking prepared positions without support, equipment disparity',
    confidence: 'HIGH'
  },
  SEVERELY_INFERIOR: {
    value: 'SEVERELY_INFERIOR',
    label: 'Severely Inferior',
    description: 'Major capability gaps, tactical disadvantage',
    friendly_casualty_multiplier: 1.70,
    enemy_casualty_multiplier: 0.60,
    exchange_ratio: '4:1 to 7:1 against',
    examples: 'Russian assaults on prepared Ukrainian positions, human wave tactics',
    confidence: 'MEDIUM',
    warning: 'Unsustainable casualty rates expected'
  }
};

// ============================================================================
// EXPERIENCE / TIME IN COMBAT MODIFIERS
// ============================================================================

/**
 * Combat experience dramatically affects survival, especially early
 * 
 * Research basis:
 * - Mediazona: 50%+ of mobilized deaths within 5 months
 * - First 90 days = peak vulnerability period
 * - Survival rate improves exponentially with experience
 */
export const EXPERIENCE_MODIFIERS = {
  FIRST_30_DAYS: {
    value: 'FIRST_30_DAYS',
    label: '0-30 Days in Combat',
    description: 'Highest vulnerability period, learning survival skills',
    casualty_multiplier: 2.50,       // 2.5x baseline casualties
    survival_factor: 0.40,           // 40% survival skills vs baseline
    confidence: 'HIGH',
    warning: 'Peak casualty period - expect high losses'
  },
  DAYS_31_90: {
    value: 'DAYS_31_90',
    label: '31-90 Days in Combat',
    description: 'Rapidly learning but still vulnerable',
    casualty_multiplier: 1.80,
    survival_factor: 0.60,
    confidence: 'HIGH'
  },
  DAYS_91_180: {
    value: 'DAYS_91_180',
    label: '91-180 Days in Combat',
    description: 'Developing effective combat skills',
    casualty_multiplier: 1.30,
    survival_factor: 0.80,
    confidence: 'HIGH'
  },
  OVER_180_DAYS: {
    value: 'OVER_180_DAYS',
    label: '180+ Days in Combat',
    description: 'Experienced survivor, knows threat environment',
    casualty_multiplier: 1.00,       // BASELINE
    survival_factor: 1.00,
    confidence: 'HIGH'
  },
  OVER_365_DAYS: {
    value: 'OVER_365_DAYS',
    label: '365+ Days in Combat',
    description: 'Highly experienced, but watch for combat fatigue',
    casualty_multiplier: 0.85,
    survival_factor: 1.15,
    fatigue_risk: 1.20,              // Increased psychological casualties
    confidence: 'MEDIUM'
  }
};

// ============================================================================
// TECHNOLOGY DOMAIN MODIFIERS (Ukraine 2022-2024)
// ============================================================================

/**
 * Technology environment modifiers - critical for modern warfare
 * 
 * Research basis:
 * - 70-80% of Ukraine casualties from drones (2024 data)
 * - Persistent surveillance eliminates safe areas
 * - EW capability critical for drone defense
 */
export const TECHNOLOGY_ENVIRONMENT = {
  DRONE_SATURATED: {
    value: 'DRONE_SATURATED',
    label: 'Drone Saturated Battlefield',
    description: 'Ubiquitous FPV drones, constant surveillance, no safe rear',
    casualty_multiplier: 1.50,       // 50% higher casualties
    rear_area_multiplier: 2.00,      // Rear is no longer safe
    kia_share_multiplier: 2.00,      // EDUCATED GUESS (2026-10-11): multiplies the immediate killed share of casualties (not DOW); see docs/research/RESEARCH_ARMOR_EQUIPMENT.md section 4
    movement_multiplier: 1.80,       // Moving is deadly
    examples: 'Bakhmut 2023, Avdiivka 2024, current front lines',
    confidence: 'HIGH',
    source: 'Ukraine 2024: 70-80% casualties from drones'
  },
  DRONE_CONTESTED: {
    value: 'DRONE_CONTESTED',
    label: 'Contested Drone Environment',
    description: 'Both sides have drone capability, EW provides some protection',
    casualty_multiplier: 1.25,
    rear_area_multiplier: 1.50,
    kia_share_multiplier: 1.30,      // EDUCATED GUESS (2026-10-11): multiplies the immediate killed share of casualties (not DOW); see docs/research/RESEARCH_ARMOR_EQUIPMENT.md section 4
    movement_multiplier: 1.40,
    examples: 'Standard modern conventional warfare',
    confidence: 'HIGH'
  },
  DRONE_ADVANTAGE: {
    value: 'DRONE_ADVANTAGE',
    label: 'Friendly Drone Superiority',
    description: 'We have drone/ISR superiority, enemy movements detected',
    casualty_multiplier: 0.90,       // Better awareness = fewer friendly casualties
    rear_area_multiplier: 1.20,
    kia_share_multiplier: 1.00,      // EDUCATED GUESS (2026-10-11): multiplies the immediate killed share of casualties (not DOW); see docs/research/RESEARCH_ARMOR_EQUIPMENT.md section 4
    movement_multiplier: 1.10,
    enemy_multiplier: 1.40,          // Enemy suffers more
    examples: 'Air superiority with drone integration',
    confidence: 'MEDIUM'
  },
  CONVENTIONAL: {
    value: 'CONVENTIONAL',
    label: 'Conventional (Limited Drones)',
    description: 'Traditional battlefield with limited drone presence',
    casualty_multiplier: 1.00,       // BASELINE (historical norms)
    rear_area_multiplier: 1.00,
    kia_share_multiplier: 1.00,      // EDUCATED GUESS (2026-10-11): multiplies the immediate killed share of casualties (not DOW); see docs/research/RESEARCH_ARMOR_EQUIPMENT.md section 4
    movement_multiplier: 1.00,
    examples: 'Pre-2020 warfare, limited technology environments',
    confidence: 'HIGH'
  },
  EW_PROTECTED: {
    value: 'EW_PROTECTED',
    label: 'Strong EW Protection',
    description: 'Effective electronic warfare umbrella reduces drone threat',
    casualty_multiplier: 0.85,
    rear_area_multiplier: 1.10,
    kia_share_multiplier: 1.00,      // EDUCATED GUESS (2026-10-11): multiplies the immediate killed share of casualties (not DOW); see docs/research/RESEARCH_ARMOR_EQUIPMENT.md section 4
    movement_multiplier: 0.90,
    examples: 'Units with dedicated EW support, Russian Krasukha coverage',
    confidence: 'MEDIUM'
  }
};

// ============================================================================
// UNIT RECOVERY / POST-CASUALTY STATUS
// ============================================================================

/**
 * Unit effectiveness after sustaining casualties
 * 
 * Research basis:
 * - BTG became ineffective at 30% losses
 * - Cohesion recovery takes 4-8 weeks
 * - Replacement integration degrades effectiveness temporarily
 */
export const UNIT_RECOVERY_STATUS = {
  FRESH: {
    value: 'FRESH',
    label: 'Fresh / Full Strength',
    description: 'At or near TOE strength, no recent heavy casualties',
    effectiveness_multiplier: 1.00,
    casualty_susceptibility: 1.00,
    confidence: 'HIGH'
  },
  LIGHT_CASUALTIES: {
    value: 'LIGHT_CASUALTIES',
    label: 'Light Casualties (5-15%)',
    description: 'Moderate losses, unit still effective',
    effectiveness_multiplier: 0.90,
    casualty_susceptibility: 1.05,
    confidence: 'HIGH'
  },
  MODERATE_CASUALTIES: {
    value: 'MODERATE_CASUALTIES',
    label: 'Moderate Casualties (15-30%)',
    description: 'Significant losses, degraded capability',
    effectiveness_multiplier: 0.75,
    casualty_susceptibility: 1.15,
    confidence: 'HIGH',
    warning: 'Consider rotation or reinforcement'
  },
  HEAVY_CASUALTIES: {
    value: 'HEAVY_CASUALTIES',
    label: 'Heavy Casualties (30-50%)',
    description: 'Severe losses, combat effectiveness seriously degraded',
    effectiveness_multiplier: 0.50,
    casualty_susceptibility: 1.30,
    confidence: 'HIGH',
    warning: 'Unit may be combat ineffective for offensive operations'
  },
  COMBAT_INEFFECTIVE: {
    value: 'COMBAT_INEFFECTIVE',
    label: 'Combat Ineffective (>50%)',
    description: 'Unit shattered, requires complete reconstitution',
    effectiveness_multiplier: 0.25,
    casualty_susceptibility: 1.50,
    confidence: 'HIGH',
    warning: 'CRITICAL: Unit should be withdrawn for reconstitution'
  },
  RECONSTITUTING: {
    value: 'RECONSTITUTING',
    label: 'Reconstituting',
    description: 'Receiving replacements, rebuilding cohesion',
    effectiveness_multiplier: 0.60,
    casualty_susceptibility: 1.25,    // New troops = higher risk
    weeks_to_full: 4,
    confidence: 'MEDIUM'
  }
};

// ============================================================================
// MEDICAL CAPABILITY MODIFIERS
// ============================================================================

/**
 * Medical capability affects DOW and RTD rates
 * 
 * Research basis:
 * - TCCC: 44% mortality reduction
 * - Golden Hour: KIA 16.0% → 9.9%
 * - Tourniquet pre-shock: 90% vs 0% survival
 * - Ukraine W:K ratio differential: 40% better outcomes with superior medical
 */
export const MEDICAL_CAPABILITY = {
  EXCELLENT: {
    value: 'EXCELLENT',
    label: 'Excellent (Full NATO Standard)',
    description: 'Complete TCCC, Golden Hour capability, full Role 1-3, blood products',
    dow_multiplier: 0.40,            // 60% fewer DOW
    rtd_improvement: 1.30,           // 30% more RTD
    wia_survival: 0.95,              // 95% of WIA survive
    examples: 'US Army medical system, NATO with full support',
    confidence: 'HIGH'
  },
  GOOD: {
    value: 'GOOD',
    label: 'Good (Standard NATO)',
    description: 'TCCC trained, evacuation within 2 hours, adequate surgical capacity',
    dow_multiplier: 0.60,
    rtd_improvement: 1.15,
    wia_survival: 0.92,
    examples: 'Standard Romanian military medical, most NATO forces',
    confidence: 'HIGH'
  },
  ADEQUATE: {
    value: 'ADEQUATE',
    label: 'Adequate',
    description: 'Basic trauma care, evacuation within 4 hours',
    dow_multiplier: 1.00,            // BASELINE
    rtd_improvement: 1.00,
    wia_survival: 0.85,
    examples: 'Stretched medical services, limited helicopter evacuation',
    confidence: 'HIGH'
  },
  LIMITED: {
    value: 'LIMITED',
    label: 'Limited',
    description: 'Delayed evacuation, limited surgical capability',
    dow_multiplier: 1.30,            // 30% more DOW
    rtd_improvement: 0.85,
    wia_survival: 0.75,
    examples: 'Extended evacuation times, overloaded medical system',
    confidence: 'HIGH'
  },
  POOR: {
    value: 'POOR',
    label: 'Poor',
    description: 'Minimal medical support, evacuation >6 hours',
    dow_multiplier: 1.60,
    rtd_improvement: 0.70,
    wia_survival: 0.60,
    examples: 'Russian medical system 2022, MASCAL situations',
    confidence: 'MEDIUM'
  },
  COLLAPSED: {
    value: 'COLLAPSED',
    label: 'Collapsed / No Support',
    description: 'Medical system overwhelmed or absent',
    dow_multiplier: 2.50,            // 2.5x more DOW
    rtd_improvement: 0.40,
    wia_survival: 0.40,
    examples: 'Encircled units, total MASCAL, evacuation impossible',
    confidence: 'MEDIUM',
    warning: 'CATASTROPHIC - most wounded will die'
  }
};

// ============================================================================
// CC-005: UNIT STRENGTH EFFECTIVENESS THRESHOLDS
// ============================================================================

/**
 * Unit Combat Effectiveness by Strength Percentage
 * 
 * Based on doctrinal assessment criteria and historical analysis.
 * Used by strengthProjectionModule for threshold warnings and G1 requests.
 * 
 * Doctrinal Reference:
 * - FM 12-6: Personnel Doctrine - Casualty Operations
 * - ATP 4-02.55: AHS Support Planning
 * - Dupuy Institute: Combat effectiveness research
 */
export const STRENGTH_EFFECTIVENESS = {
  FULL_EFFECTIVE: {
    code: 'FULL',
    label: 'Full Combat Effective',
    percent_min: 85,
    percent_max: 100,
    color: 'success',
    icon: '🟢',
    description: 'Full combat effective - all missions capable',
    capabilities: ['Offensive operations', 'Main attack', 'Defense', 'Exploitation'],
    action_required: 'None - maintain readiness'
  },
  REDUCED_CAPABILITY: {
    code: 'REDUCED',
    label: 'Reduced Capability',
    percent_min: 70,
    percent_max: 84,
    color: 'warning',
    icon: '🟡',
    description: 'Reduced capability - cannot conduct main attack',
    capabilities: ['Supporting attack', 'Defense', 'Reserve'],
    action_required: 'Request replacements, avoid decisive engagement'
  },
  MISSION_DEGRADED: {
    code: 'DEGRADED',
    label: 'Mission Degraded',
    percent_min: 50,
    percent_max: 69,
    color: 'error',
    icon: '🟠',
    description: 'Mission degraded - defensive operations only',
    capabilities: ['Defense in sector', 'Delay', 'Reserve'],
    action_required: 'Priority replacement request, limit exposure'
  },
  COMBAT_INEFFECTIVE: {
    code: 'INEFFECTIVE',
    label: 'Combat Ineffective',
    percent_min: 30,
    percent_max: 49,
    color: 'error',
    icon: '🔴',
    description: 'Combat ineffective - reconstitution required',
    capabilities: ['Security tasks only', 'Reconstitution'],
    action_required: 'Emergency reconstitution, withdraw from line'
  },
  DESTROYED: {
    code: 'DESTROYED',
    label: 'Unit Destroyed',
    percent_min: 0,
    percent_max: 29,
    color: 'error',
    icon: '⬛',
    description: 'Unit destroyed - merge with another unit',
    capabilities: ['None - administrative only'],
    action_required: 'Deactivate or merge with sister unit'
  }
};

/**
 * Thresholds for Triggering Replacement Requests
 * 
 * These thresholds drive automatic G1 request generation in strengthProjectionModule
 */
export const REPLACEMENT_THRESHOLDS = {
  // Percentage thresholds
  WARNING: 85,          // Amber alert - monitor closely
  TRIGGER: 70,          // Request replacements (default threshold)
  CRITICAL: 50,         // Priority/emergency request
  EMERGENCY: 30,        // Unit combat ineffective
  
  // Named threshold configs for UI
  LEVELS: {
    GREEN: {
      min_percent: 85,
      label: 'Green - Adequate',
      action: 'No action required',
      icon: '🟢'
    },
    AMBER: {
      min_percent: 70,
      max_percent: 84,
      label: 'Amber - Warning',
      action: 'Submit routine replacement request',
      icon: '🟡'
    },
    RED: {
      min_percent: 50,
      max_percent: 69,
      label: 'Red - Critical',
      action: 'Submit priority replacement request',
      icon: '🔴'
    },
    BLACK: {
      min_percent: 0,
      max_percent: 49,
      label: 'Black - Emergency',
      action: 'Emergency reconstitution required',
      icon: '⬛'
    }
  }
};

/**
 * Personnel Replacement Lead Times
 * 
 * Time from G1 request submission to arrival at unit.
 * Used to calculate when to submit requests to meet threshold dates.
 * 
 * Doctrinal Reference:
 * - FM 12-6: Personnel Doctrine
 * - ATP 4-02.55: AHS Support Planning
 */
export const REPLACEMENT_LEAD_TIMES = {
  COMBAT: {
    code: 'COMBAT',
    label: 'Combat Personnel',
    days: 5,
    hours: 120,
    description: 'Reserve pool → Replacement Bn → Brigade → Unit',
    includes: ['Infantry', 'Armor', 'Artillery', 'Engineer'],
    priority_modifier: 1.0
  },
  MEDICAL: {
    code: 'MEDICAL',
    label: 'Medical Personnel',
    days: 4,
    hours: 96,
    description: 'Medical reserve → Medical Bn → Assignment',
    includes: ['Physician', 'PA', 'Nurse', 'Medic', '68W'],
    priority_modifier: 1.2  // Higher priority for medical
  },
  TECHNICAL: {
    code: 'TECHNICAL',
    label: 'Technical Specialists',
    days: 7,
    hours: 168,
    description: 'Specialist pool → Training validation → Unit',
    includes: ['Communications', 'Maintenance', 'Intelligence'],
    priority_modifier: 0.9
  },
  OFFICER: {
    code: 'OFFICER',
    label: 'Officers',
    days: 7,
    hours: 168,
    description: 'Officer pool → Branch assignment → Unit',
    includes: ['Company grade', 'Field grade'],
    priority_modifier: 1.1
  },
  NCO: {
    code: 'NCO',
    label: 'NCO/Senior Enlisted',
    days: 5,
    hours: 120,
    description: 'NCO pool → Assignment → Unit',
    includes: ['E5-E9', 'Sergeant Major'],
    priority_modifier: 1.15  // Critical for unit effectiveness
  }
};

/**
 * Return to Duty (RTD) Rates by Category
 * 
 * Percentage of casualties returning to duty within timeframe.
 * Used by strengthProjectionModule for strength recovery calculations.
 */
export const RTD_RATES = {
  // T3 (Minor Wounds) - highest RTD
  T3_24H: 0.30,           // 30% return within 24 hours
  T3_72H: 0.50,           // 50% additional within 72 hours
  T3_7D: 0.15,            // 15% additional within 7 days
  T3_TOTAL: 0.95,         // 95% total RTD for T3
  
  // T2 (Moderate Wounds) - delayed RTD
  T2_7D: 0.20,            // 20% return within 7 days
  T2_14D: 0.30,           // 30% additional within 14 days
  T2_30D: 0.25,           // 25% additional within 30 days
  T2_TOTAL: 0.75,         // 75% total RTD for T2
  
  // DNBI - varies by cause
  DNBI_3D: 0.40,          // 40% return within 3 days (minor illness)
  DNBI_7D: 0.30,          // 30% additional within 7 days
  DNBI_TOTAL: 0.70,       // 70% total RTD for DNBI
  
  // Combat Stress Reaction
  CSR_72H: 0.70,          // 70% return within 72 hours (with treatment)
  CSR_7D: 0.15,           // 15% additional within 7 days
  CSR_TOTAL: 0.85         // 85% total RTD for CSR
};

/**
 * Get strength effectiveness rating for a given percentage
 * @param {number} percent - Current strength as percentage (0-100)
 * @returns {Object} Effectiveness rating object
 */
export function getStrengthEffectivenessRating(percent) {
  if (percent >= STRENGTH_EFFECTIVENESS.FULL_EFFECTIVE.percent_min) {
    return STRENGTH_EFFECTIVENESS.FULL_EFFECTIVE;
  }
  if (percent >= STRENGTH_EFFECTIVENESS.REDUCED_CAPABILITY.percent_min) {
    return STRENGTH_EFFECTIVENESS.REDUCED_CAPABILITY;
  }
  if (percent >= STRENGTH_EFFECTIVENESS.MISSION_DEGRADED.percent_min) {
    return STRENGTH_EFFECTIVENESS.MISSION_DEGRADED;
  }
  if (percent >= STRENGTH_EFFECTIVENESS.COMBAT_INEFFECTIVE.percent_min) {
    return STRENGTH_EFFECTIVENESS.COMBAT_INEFFECTIVE;
  }
  return STRENGTH_EFFECTIVENESS.DESTROYED;
}

/**
 * Get replacement threshold level for a given percentage
 * @param {number} percent - Current strength as percentage (0-100)
 * @returns {Object} Threshold level info
 */
export function getReplacementThresholdLevel(percent) {
  if (percent >= REPLACEMENT_THRESHOLDS.WARNING) {
    return REPLACEMENT_THRESHOLDS.LEVELS.GREEN;
  }
  if (percent >= REPLACEMENT_THRESHOLDS.TRIGGER) {
    return REPLACEMENT_THRESHOLDS.LEVELS.AMBER;
  }
  if (percent >= REPLACEMENT_THRESHOLDS.CRITICAL) {
    return REPLACEMENT_THRESHOLDS.LEVELS.RED;
  }
  return REPLACEMENT_THRESHOLDS.LEVELS.BLACK;
}

/**
 * Calculate days until unit reaches threshold
 * @param {number} currentPercent - Current strength percentage
 * @param {number} dailyLossRate - Average daily loss rate (percentage points)
 * @param {number} threshold - Target threshold percentage (default 70%)
 * @returns {number} Days until threshold, or Infinity if won't reach
 */
export function calculateDaysToThreshold(currentPercent, dailyLossRate, threshold = 70) {
  if (dailyLossRate <= 0) return Infinity;
  if (currentPercent <= threshold) return 0;
  
  return Math.ceil((currentPercent - threshold) / dailyLossRate);
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate combined force quality modifier
 * Uses multiplicative stacking with reasonable caps
 * 
 * @param {Object} forceQuality - Force quality configuration from scenario
 * @returns {Object} Combined modifiers and breakdown
 */
export function calculateForceQualityModifier(forceQuality) {
  if (!forceQuality) {
    return {
      combined_casualty_multiplier: 1.0,
      combined_kia_multiplier: 1.0,
      combined_dow_multiplier: 1.0,
      breakdown: { note: 'No force quality data - using baseline' }
    };
  }

  // Get individual modifiers with fallbacks
  const training = TRAINING_LEVELS[forceQuality.training_level] || TRAINING_LEVELS.REGULAR;
  const equipment = EQUIPMENT_LEVELS[forceQuality.equipment_level] || EQUIPMENT_LEVELS.ADEQUATE;
  const morale = MORALE_LEVELS[forceQuality.morale_level] || MORALE_LEVELS.NORMAL;
  const superiority = FORCE_SUPERIORITY[forceQuality.force_superiority] || FORCE_SUPERIORITY.EQUAL;
  const experience = EXPERIENCE_MODIFIERS[forceQuality.experience_level] || EXPERIENCE_MODIFIERS.OVER_180_DAYS;
  const technology = TECHNOLOGY_ENVIRONMENT[forceQuality.technology_env] || TECHNOLOGY_ENVIRONMENT.CONVENTIONAL;
  const medical = MEDICAL_CAPABILITY[forceQuality.medical_capability] || MEDICAL_CAPABILITY.ADEQUATE;

  // Calculate raw combined multiplier
  let rawCasualtyMod = training.casualty_multiplier *
                       equipment.casualty_multiplier *
                       morale.casualty_multiplier *
                       superiority.friendly_casualty_multiplier *
                       experience.casualty_multiplier *
                       technology.casualty_multiplier;

  // Cap to prevent unrealistic extremes (0.2x to 5.0x range)
  const combinedCasualtyMod = Math.max(0.20, Math.min(5.0, rawCasualtyMod));

  // KIA-specific modifier (equipment dominates)
  let rawKiaMod = equipment.kia_multiplier *
                  (morale.casualty_multiplier * 0.5 + 0.5); // Morale has half effect on KIA
  const combinedKiaMod = Math.max(0.15, Math.min(3.0, rawKiaMod));

  // DOW-specific modifier (medical capability dominates)
  let rawDowMod = medical.dow_multiplier *
                  equipment.dow_multiplier *
                  (technology.casualty_multiplier * 0.3 + 0.7); // Tech has 30% effect on DOW
  const combinedDowMod = Math.max(0.20, Math.min(4.0, rawDowMod));

  return {
    combined_casualty_multiplier: Math.round(combinedCasualtyMod * 100) / 100,
    combined_kia_multiplier: Math.round(combinedKiaMod * 100) / 100,
    combined_dow_multiplier: Math.round(combinedDowMod * 100) / 100,
    breakdown: {
      training: {
        level: training.label,
        multiplier: training.casualty_multiplier
      },
      equipment: {
        level: equipment.label,
        multiplier: equipment.casualty_multiplier,
        kia_effect: equipment.kia_multiplier
      },
      morale: {
        level: morale.label,
        multiplier: morale.casualty_multiplier
      },
      force_superiority: {
        level: superiority.label,
        multiplier: superiority.friendly_casualty_multiplier
      },
      experience: {
        level: experience.label,
        multiplier: experience.casualty_multiplier
      },
      technology: {
        environment: technology.label,
        multiplier: technology.casualty_multiplier
      },
      medical: {
        capability: medical.label,
        dow_multiplier: medical.dow_multiplier
      }
    },
    warnings: collectWarnings(training, equipment, morale, superiority, experience, technology, medical)
  };
}

/**
 * Collect any warnings from selected modifiers
 */
function collectWarnings(training, equipment, morale, superiority, experience, technology, medical) {
  const warnings = [];
  
  [training, equipment, morale, superiority, experience, technology, medical].forEach(mod => {
    if (mod.warning) {
      warnings.push({
        source: mod.label,
        message: mod.warning
      });
    }
  });
  
  return warnings;
}

/**
 * Get enemy casualty modifier based on force quality differential
 * 
 * @param {Object} friendlyQuality - Our force quality
 * @param {Object} enemyQuality - Enemy force quality
 * @returns {number} Multiplier for enemy casualties
 */
export function getEnemyCasualtyModifier(friendlyQuality, enemyQuality) {
  const friendly = TRAINING_LEVELS[friendlyQuality?.training_level] || TRAINING_LEVELS.REGULAR;
  const enemy = TRAINING_LEVELS[enemyQuality?.training_level] || TRAINING_LEVELS.REGULAR;
  
  // Ratio of effectiveness values
  const qualityRatio = friendly.effectiveness_value / enemy.effectiveness_value;
  
  // Apply friendly inflict bonus
  const inflictMod = friendly.inflict_multiplier || 1.0;
  
  // Combined modifier for enemy casualties
  return Math.round(qualityRatio * inflictMod * 100) / 100;
}

/**
 * Determine break probability based on casualties and morale
 * 
 * @param {number} casualtyRate - Fraction of unit as casualties (0-1)
 * @param {string} moraleLevel - Morale level key
 * @returns {Object} Break probability and status
 */
export function assessBreakProbability(casualtyRate, moraleLevel) {
  const morale = MORALE_LEVELS[moraleLevel] || MORALE_LEVELS.NORMAL;
  const threshold = morale.break_threshold;
  
  if (casualtyRate < threshold * 0.5) {
    return { probability: 0.05, status: 'STABLE', message: 'Unit stable' };
  }
  if (casualtyRate < threshold * 0.75) {
    return { probability: 0.15, status: 'STRESSED', message: 'Unit under strain' };
  }
  if (casualtyRate < threshold) {
    return { probability: 0.35, status: 'CRITICAL', message: 'Unit approaching break point' };
  }
  if (casualtyRate < threshold * 1.25) {
    return { probability: 0.60, status: 'BREAKING', message: 'HIGH probability of unit break' };
  }
  return { probability: 0.85, status: 'SHATTERED', message: 'Unit likely combat ineffective' };
}

// ============================================================================
// CLS TRAINING LEVELS
// ============================================================================

/**
 * Combat Lifesaver training levels - affects DOW (Died of Wounds) rate through buddy care quality.
 * Moved from Tab1_ForceComposition.jsx (local constant) to constants layer (v4.3.5 cleanup).
 * Used in Force Quality configuration; cls_training_percentage will wire into calculator in future phase.
 */
export const CLS_TRAINING_LEVELS = {
  LOW: {
    label: 'Low (10%)',
    percentage: 10,
    dow_multiplier: 1.15,  // +15% DOW due to poor buddy care
    description: 'Minimal CLS training - only medics'
  },
  STANDARD: {
    label: 'Standard (25%)',
    percentage: 25,
    dow_multiplier: 1.0,   // Baseline
    description: 'NATO standard - 1 CLS per squad'
  },
  HIGH: {
    label: 'High (40%)',
    percentage: 40,
    dow_multiplier: 0.85,  // -15% DOW through better buddy care
    description: 'Enhanced training - multiple CLS per squad'
  },
  ELITE: {
    label: 'Elite (60%)',
    percentage: 60,
    dow_multiplier: 0.70,  // -30% DOW - exceptional buddy care
    description: 'Special operations level - most soldiers CLS trained'
  }
};

// ============================================================================
// DEFAULT FORCE QUALITY
// ============================================================================

/**
 * Default force quality values applied at Corps level, cascading to all subordinate units.
 * Moved from Tab1_ForceComposition.jsx (local constant) to constants layer (v4.3.5 cleanup).
 * Keys must match fields in TRAINING_LEVELS, EQUIPMENT_LEVELS, MORALE_LEVELS,
 * EXPERIENCE_MODIFIERS, TECHNOLOGY_ENVIRONMENT, MEDICAL_CAPABILITY, and CLS_TRAINING_LEVELS.
 */
export const DEFAULT_FORCE_QUALITY = {
  training_level: 'REGULAR',
  equipment_level: 'ADEQUATE',
  morale_level: 'NORMAL',
  experience_level: 'OVER_180_DAYS',
  technology_env: 'DRONE_CONTESTED',
  medical_capability: 'GOOD',
  cls_training_percentage: 'STANDARD'
};

/**
 * Returns a fresh copy of DEFAULT_FORCE_QUALITY to prevent mutation
 * Used by Tab1 for initializing force quality with independent objects
 */
export const getDefaultForceQuality = () => ({
  ...DEFAULT_FORCE_QUALITY
});

/**
 * ENH-043: Resolve per-brigade force quality by walking Corps → Division → Brigade override chain.
 *
 * The calculator's corps-level `force.force_quality` is the Tab1 corps baseline (already set by
 * the user in the Force Quality panel). This function then applies any division-level and
 * brigade-level overrides configured via Tab1 UnitEditDialog → ForceQualityOverrideSection,
 * stored as `force_quality_override` on each unit in `forceBuilderResult`.
 *
 * Precedence (lowest → highest):
 *   DEFAULT_FORCE_QUALITY → force.force_quality (corps) → division override → brigade override
 *
 * Used by casualtyCalculator.js calculateBrigadeCasualties() to replace:
 *   forceQuality: force?.force_quality || {}   ← single global object (pre-ENH-043)
 * with:
 *   forceQuality: resolvePerBrigadeQuality(normalizedBrigadeConfig, force)
 *
 * @param {Object} brigadeConfig - Brigade being calculated (must carry .id)
 * @param {Object} force        - force object from scenario (has .force_quality + .forceBuilderResult)
 * @returns {Object}            - Fully-resolved force quality for this brigade
 */
/**
 * Tab 1 CLS training level -> the first-aid quality key used by the survival model
 * (operationsConfig CLS_TRAINING_QUALITY: UNTRAINED / BASIC / STANDARD / ADVANCED).
 * Tab 1 saves forceQuality.cls_training_percentage (LOW / STANDARD / HIGH / ELITE); before 2026-10-10 the calculator
 * never read it (it read forceQuality.cls_training, which no tab writes) so the Tab 1 input had no effect.
 * PLACEHOLDER mapping, to be confirmed by Dr. Tatar: HIGH and ELITE both map to ADVANCED (the model has four levels).
 * The dow_multiplier on CLS_TRAINING_LEVELS is NOT applied: first-aid quality already acts through time to first
 * intervention, TXA probability and survival modifier, and a second multiplier would count it twice.
 */
export const CLS_TRAINING_LEVEL_TO_QUALITY = {
  LOW: 'BASIC',
  STANDARD: 'STANDARD',
  HIGH: 'ADVANCED',
  ELITE: 'ADVANCED'
};

/**
 * First-aid quality key for a resolved force quality object. Throws on an unknown Tab 1 level;
 * an absent level means the planner never set it and takes the Tab 1 default (STANDARD).
 */
export const resolveClsQuality = (forceQuality) => {
  const level = forceQuality?.cls_training_percentage ?? DEFAULT_FORCE_QUALITY.cls_training_percentage;
  if (!Object.prototype.hasOwnProperty.call(CLS_TRAINING_LEVEL_TO_QUALITY, level)) {
    throw new Error(`resolveClsQuality: unknown cls_training_percentage '${level}' (valid: ${Object.keys(CLS_TRAINING_LEVEL_TO_QUALITY).join(', ')})`);
  }
  return CLS_TRAINING_LEVEL_TO_QUALITY[level];
};

export const resolvePerBrigadeQuality = (brigadeConfig, force) => {
  // Corps-level baseline: DEFAULT merged with whatever the user configured at corps level in Tab1
  const base = { ...DEFAULT_FORCE_QUALITY, ...(force?.force_quality || {}) };

  const fbr = force?.forceBuilderResult;
  if (!fbr || !brigadeConfig?.id) return base;

  let resolved = { ...base };

  // Find the brigade entry in forceBuilderResult (by id) to access its stored overrides
  const brigade = fbr.brigades?.find(b => b.id === brigadeConfig.id);
  if (!brigade) return resolved;

  // Apply division-level override (if this brigade belongs to a division)
  if (brigade.parent_division_id) {
    const division = fbr.divisions?.find(d => d.id === brigade.parent_division_id);
    if (division?.force_quality_override) {
      Object.entries(division.force_quality_override).forEach(([key, val]) => {
        if (val !== null && val !== undefined) resolved[key] = val;
      });
    }
  }

  // Apply brigade-level override (highest priority — most specific)
  if (brigade.force_quality_override) {
    Object.entries(brigade.force_quality_override).forEach(([key, val]) => {
      if (val !== null && val !== undefined) resolved[key] = val;
    });
  }

  return resolved;
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const combatEffectivenessModifiers = {
  TRAINING_LEVELS,
  EQUIPMENT_LEVELS,
  MORALE_LEVELS,
  FORCE_SUPERIORITY,
  EXPERIENCE_MODIFIERS,
  TECHNOLOGY_ENVIRONMENT,
  UNIT_RECOVERY_STATUS,
  MEDICAL_CAPABILITY,
  
  // CC-005: Strength Effectiveness
  STRENGTH_EFFECTIVENESS,
  REPLACEMENT_THRESHOLDS,
  REPLACEMENT_LEAD_TIMES,
  RTD_RATES,
  
  // Helper functions
  calculateForceQualityModifier,
  getEnemyCasualtyModifier,
  assessBreakProbability,
  getStrengthEffectivenessRating,
  getReplacementThresholdLevel,
  calculateDaysToThreshold,

  // Force quality defaults (moved from Tab1 v4.3.5)
  CLS_TRAINING_LEVELS,
  DEFAULT_FORCE_QUALITY,
  getDefaultForceQuality,

  // ENH-043: Per-brigade force quality resolver (used by casualtyCalculator.js)
  resolvePerBrigadeQuality
};

export default combatEffectivenessModifiers;


/**
 * Evidence status of the force quality multipliers (2026-10-11, Dr. Tatar: label what is an educated guess).
 * No source gives these values; the direction is supported by history, the size is a guess to be discussed and adjusted.
 * Research: docs/research/RESEARCH_ARMOR_EQUIPMENT.md. Base casualty rate anchor (1 to 3 percent a day, Dupuy) is separate.
 */
export const FORCE_QUALITY_PARAMETER_STATUS = {
  training:    { status: 'EDUCATED_GUESS', basis: 'Direction from Dupuy combat effectiveness value; multipliers 0.6 to 2.5 unsourced.' },
  equipment:   { status: 'EDUCATED_GUESS', basis: 'Casualty multiplier unsourced; the killed share of the mounted part of the units follows the vehicle protection figures of the Defense Department (MRAP 6, Abrams 15, up-armored HMMWV 22 percent casualty rate; contested), the levels and mounted shares are guesses; no study ties equipment to died of wounds (not wired).' },
  morale:      { status: 'EDUCATED_GUESS', basis: 'Direction only; cohesion factors on combat stress 0.7 to 2.0 carried over from the old literal.' },
  experience:  { status: 'EDUCATED_GUESS', basis: 'Anecdotal evidence that replacements suffer most in their first days; multipliers 2.5 to 0.85 unsourced.' },
  technology:  { status: 'EDUCATED_GUESS', basis: 'Casualty multiplier unsourced; killed-share multiplier anchored on unverified Ukrainian claims (killed share 62 to 70 percent, dead to seriously wounded about 1 to 1) against 1 to 3 to 1 to 5 in traditional wars.' },
  unit_type:   { status: 'EDUCATED_GUESS', basis: 'Direction supported (infantry takes most combat deaths); modifiers 0.2 to 1.3 unsourced.' },
  rear_area:   { status: 'EDUCATED_GUESS', basis: 'Distance bands and vulnerability factors unsourced; qualitative support only (FPV kill zone up to 20 km, brigade support area 10 to 30 km).' }
};
